<template>
  <div
    :class="$style.base"
    v-test="'sales-search'"
  >
    <BaseSearch
      v-model="term"
      :placeholder="placeholder"
      :debounce="isOrderNumberTerm"
    />
    <CustomerResultList
      :show="isExpanded"
      :items="customers"
      :loading="isLookupPending"
      @select="selectCustomer"
      @close="isExpanded = false"
      v-test="'sales-search-list'"
    >
      <template #item="{ item }">
        <div
          :class="$style.row"
          v-test="'sales-search-customer'"
        >
          <BaseAvatar
            :name="item.firstName"
            :url="item.picture"
            :class="$style.avatar"
          />
          <div :class="$style.info">
            <BaseText oneLine>{{ item.fullName }}</BaseText>
            <div
              v-if="item.email || item.mobilePhone"
              :class="$style.details"
            >
              {{ [item.email, item.mobilePhone].filter(Boolean).join(' - ') }}
            </div>
          </div>
        </div>
      </template>
    </CustomerResultList>
  </div>
</template>

<script
  setup
  lang="ts"
>
import { useQuery } from '@vue/apollo-composable';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import CustomerResultList from '@/components/customer-search/CustomerResultList.vue';
import type { Customer } from '@/types';
import { SALES_SEARCH_CUSTOMERS_QUERY } from './graphql';

defineProps<{ placeholder?: string }>();

const emit = defineEmits<{
  (e: 'update:search', term: string): void;
  (e: 'update:customerId', customerId: number | null): void;
}>();

const ORDER_NUMBER_TERM = /^[\d-]+$/;
const DEBOUNCE_MS = 700;

const term = ref('');
const selectedCustomer = ref<Customer | null>(null);
const isExpanded = ref(false);

// While a customer is selected the input shows their name as its value.
const isShowingSelectedCustomer = computed(
  () =>
    !!selectedCustomer.value && term.value === selectedCustomer.value.fullName,
);

const isOrderNumberTerm = computed(
  () => !!term.value && ORDER_NUMBER_TERM.test(term.value),
);
const isCustomerTerm = computed(() => !!term.value && !isOrderNumberTerm.value);

watch(term, (value) => {
  if (selectedCustomer.value) {
    if (isShowingSelectedCustomer.value) return;
    // Retyping over the selected name — numbers included — drops the customer
    // filter; the new term is then routed by shape like any other input.
    selectedCustomer.value = null;
    emit('update:customerId', null);
  }
  isExpanded.value = isCustomerTerm.value;

  // Name terms arrive per keystroke and only clear the order-number search.
  // Once the term is a number the input debounces further digits itself (see
  // the :debounce binding), so the parent's list query is not run per digit;
  // a lone digit is not searched, matching the input's own minimum for
  // debounced terms.
  if (isOrderNumberTerm.value && value.length === 1) return;
  emit('update:search', isCustomerTerm.value ? '' : value);
});

const selectCustomer = (customer: Customer) => {
  selectedCustomer.value = customer;
  term.value = customer.fullName;
  isExpanded.value = false;
  emit('update:customerId', customer.id);
};

const { result, loading, query, onResult, onError } = useQuery(
  SALES_SEARCH_CUSTOMERS_QUERY,
  () => ({
    name: term.value,
    pagination: { limit: 8, offset: 0 },
  }),
  () => ({
    enabled: isCustomerTerm.value && !isShowingSelectedCustomer.value,
    debounce: DEBOUNCE_MS,
  }),
);

// The term the current result answers, taken from the variables the query was
// actually run with. While it differs from the typed term the lookup is still
// pending (debounce or request), whatever `loading` does in between and even
// when a cached answer arrives without a request.
const fetchedTerm = ref('');

const markFetched = () => {
  fetchedTerm.value = query.value?.variables?.name ?? '';
};

onResult((queryResult) => {
  if (!queryResult.loading) markFetched();
});
onError(markFetched);

const isLookupPending = computed(
  () =>
    isCustomerTerm.value && (fetchedTerm.value !== term.value || loading.value),
);

const customers = computed<Customer[]>(() => result.value?.customers ?? []);

// The component owns the filters it emits, so they must not outlive it: the
// parent keeps its variables when v-if unmounts us on a view switch, and a
// remount starts with an empty input that could never clear a stale customerId.
onBeforeUnmount(() => {
  emit('update:search', '');
  emit('update:customerId', null);
});
</script>

<style
  lang="scss"
  module
>
.base {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 230px;
}

.row {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: $spacing * 0.5;
}

.info {
  overflow: hidden;
  flex: 1;
}

.details {
  font-size: 10px;
  opacity: 0.7;
}
</style>
